import { Component, lazy, Suspense } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';
import './App.css';
import ClientRegister from './pages/Website/ClientRegister.jsx';
import DeviceConflictNotification from './components/Auth/DeviceConflictNotification.jsx';
import NotificationContainer from './components/NotificationContainer.jsx';
import PrivateRoute from './components/PrivateRoute.jsx';
import Sidebar from './components/Sidebar/Sidebar.jsx';
import TopHeader from './components/TopHeader/TopHeader.jsx';
import { AuthProvider } from './context/AuthContext.jsx';
import { LanguageProvider, useLanguage } from './context/LanguageContext.jsx';
import { NotificationProvider } from './context/NotificationContext.jsx';
import { SidebarProvider } from './context/SidebarContext';
import { ThemeProvider } from './context/ThemeContext.jsx';
import './styles/designSystem.css';

// Lazy-loaded page components for route-based code splitting
const ClientAnalytics = lazy(() => import('./components/Clients/ClientAnalytics.jsx'));
const ClientPortal = lazy(() => import('./pages/ClientPortal.jsx'));
const ClientReports = lazy(() => import('./pages/ClientReports.jsx'));
const Clients = lazy(() => import('./pages/Clients.jsx'));
const AttributesManager = lazy(() => import('./pages/coveraty/AttributesManager.jsx'));
const CategoriesManager = lazy(() => import('./pages/coveraty/CategoriesManager.jsx'));
const DesignsManager = lazy(() => import('./pages/coveraty/DesignsManager.jsx'));
const ShippingManager = lazy(() => import('./pages/coveraty/ShippingManager.jsx'));
const StoreCustomers = lazy(() => import('./pages/coveraty/StoreCustomers.jsx'));
const StoreOrders = lazy(() => import('./pages/coveraty/StoreOrders.jsx'));
const FinancialReport = lazy(() => import('./pages/FinancialReport.jsx'));
const FinancialStats = lazy(() => import('./pages/FinancialStats.jsx'));
const Home = lazy(() => import('./pages/Home.jsx'));
const Inventory = lazy(() => import('./pages/Inventory.jsx'));
const Invoices = lazy(() => import('./pages/Invoices.jsx'));
const Materials = lazy(() => import('./pages/Materials.jsx'));
const MaterialWithdrawal = lazy(() => import('./pages/MaterialWithdrawal.jsx'));
const Notifications = lazy(() => import('./pages/Notifications.jsx'));
const Orders = lazy(() => import('./pages/Orders.jsx'));
const Products = lazy(() => import('./pages/Products.jsx'));
const Purchases = lazy(() => import('./pages/Purchases.jsx'));
const Suppliers = lazy(() => import('./pages/Suppliers.jsx'));
const Unauthorized = lazy(() => import('./pages/Unauthorized.jsx'));
const Users = lazy(() => import('./pages/Users.jsx'));
const LandingPage = lazy(() => import('./pages/Website/LandingPage.jsx'));
const WebsiteLogin = lazy(() => import('./pages/Website/WebsiteLogin.jsx'));
const WebsiteSettings = lazy(() => import('./pages/WebsiteSettings.jsx'));

// Error Boundary Fallback View
const ErrorBoundaryFallback = ({ error, onReload }) => {
  const { t } = useLanguage();

  return (
    <div className="error-boundary-wrapper">
      <div className="error-boundary-card">
        <h2 className="error-boundary-title">
          {t('error.unexpected', 'An unexpected error occurred')}
        </h2>
        <p className="error-boundary-msg">
          {t('error.pageLoadError', 'An error occurred while loading this page. Please reload to try again.')}
        </p>
        {error && (
          <pre className="error-boundary-details">
            {error.message || String(error)}
          </pre>
        )}
        <button
          onClick={onReload}
          className="btn-primary"
        >
          {t('error.reload', 'Reload Page')}
        </button>
      </div>
    </div>
  );
};

// Error Boundary Component
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return <ErrorBoundaryFallback error={this.state.error} onReload={this.handleReload} />;
    }
    return this.props.children;
  }
}

// 404 Not Found Page Component
const NotFoundPage = () => {
  const { t } = useLanguage();
  return (
    <div className="not-found-container">
      <h1 className="not-found-title">{t('notFound.title', '404')}</h1>
      <p className="not-found-msg">{t('notFound.message', 'Page Not Found')}</p>
      <a href="/dashboard" className="not-found-link">{t('notFound.backHome', 'Back to Dashboard')}</a>
    </div>
  );
};

// Layout component to conditionally show sidebar and top header
const Layout = ({ children }) => {
  const location = useLocation();
  const path = location.pathname;

  const isAuthPage = path === '/login' || path === '/register' || path === '/unauthorized';
  const isWebsitePage = path === '/' || (path.startsWith('/website') && !path.startsWith('/website-settings')) || path === '/client-portal';
  const isERPage = !isAuthPage && !isWebsitePage;

  return (
    <div className="app-container">
      {isERPage && <Sidebar />}
      <main className={`main-content ${isERPage ? 'with-sidebar' : ''} ${path.startsWith('/dashboard') ? 'dashboard-page' : ''}`}>
        {isERPage && <TopHeader />}
        <div className={`content-wrapper ${isERPage ? 'with-header' : ''}`}>
          {!isWebsitePage && <DeviceConflictNotification />}
          <NotificationContainer />
          <Suspense fallback={
            <div className="flex items-center justify-center min-h-[60vh]">
              <div
                className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin"
                style={{
                  borderColor: 'var(--theme-primary, var(--primary-blue, #2563eb))',
                  borderTopColor: 'transparent',
                }}
              ></div>
            </div>
          }>
            {children}
          </Suspense>
        </div>
      </main>
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <ThemeProvider>
      <ErrorBoundary>
        <AuthProvider>
          <NotificationProvider>
            <SidebarProvider>
            <Router future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true
            }}>
              <Layout>
                <Routes>
                  {/* Public Website - Main Page */}
                  <Route path="/" element={<LandingPage />} />

                  {/* Main Login Page - Website Login */}
                  <Route path="/login" element={<WebsiteLogin />} />
                  <Route path="/register" element={<ClientRegister />} />
                  <Route path="/website/login" element={<Navigate to="/login" replace />} />

                  {/* Client Portal */}
                  <Route
                    path="/client-portal"
                    element={
                      <PrivateRoute roles={['client']}>
                        <ClientPortal />
                      </PrivateRoute>
                    }
                  />

                  {/* Other Routes */}
                  <Route path="/unauthorized" element={<Unauthorized />} />

                  {/* ERP Dashboard */}
                  <Route
                    path="/dashboard"
                    element={
                      <PrivateRoute roles={['receptionist', 'designer', 'worker', 'financial', 'admin']}>
                        <Home />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/clients"
                    element={
                      <PrivateRoute roles={['receptionist', 'admin']}>
                        <Clients />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/reports/client-analytics"
                    element={
                      <PrivateRoute roles={['financial', 'admin']}>
                        <ClientAnalytics />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/orders"
                    element={
                      <PrivateRoute roles={['designer', 'worker', 'financial', 'admin']}>
                        <Orders />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/invoices/*"
                    element={
                      <PrivateRoute roles={['designer', 'financial', 'admin']}>
                        <Invoices />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/financial-stats"
                    element={
                      <PrivateRoute roles={['financial', 'admin']}>
                        <FinancialStats />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/financial-report"
                    element={
                      <PrivateRoute roles={['financial', 'admin']}>
                        <FinancialReport />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/client-reports"
                    element={
                      <PrivateRoute roles={['admin', 'financial']}>
                        <ClientReports />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/users"
                    element={
                      <PrivateRoute roles={['admin']}>
                        <Users />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/notifications"
                    element={
                      <PrivateRoute>
                        <Notifications />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/materials"
                    element={
                      <PrivateRoute roles={['admin']}>
                        <Materials />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/products"
                    element={
                      <PrivateRoute roles={['admin', 'designer']}>
                        <Products />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/suppliers"
                    element={
                      <PrivateRoute roles={['admin']}>
                        <Suppliers />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/purchases"
                    element={
                      <PrivateRoute roles={['admin']}>
                        <Purchases />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/inventory"
                    element={
                      <PrivateRoute roles={['admin', 'worker']}>
                        <Inventory />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/material-withdrawal"
                    element={
                      <PrivateRoute roles={['admin', 'worker']}>
                        <MaterialWithdrawal />
                      </PrivateRoute>
                    }
                  />

                  <Route
                    path="/website-settings"
                    element={
                      <PrivateRoute roles={['admin']}>
                        <WebsiteSettings />
                      </PrivateRoute>
                    }
                  />

                  {/* Coveraty Store Management */}
                  <Route
                    path="/coveraty/designs"
                    element={
                      <PrivateRoute roles={['admin']}>
                        <DesignsManager />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/coveraty/attributes"
                    element={
                      <PrivateRoute roles={['admin']}>
                        <AttributesManager />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/coveraty/categories"
                    element={
                      <PrivateRoute roles={['admin']}>
                        <CategoriesManager />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/coveraty/shipping"
                    element={
                      <PrivateRoute roles={['admin']}>
                        <ShippingManager />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/coveraty/customers"
                    element={
                      <PrivateRoute roles={['admin']}>
                        <StoreCustomers />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/coveraty/orders"
                    element={
                      <PrivateRoute roles={['admin']}>
                        <StoreOrders />
                      </PrivateRoute>
                    }
                  />

                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Layout>
            </Router>
          </SidebarProvider>
        </NotificationProvider>
      </AuthProvider>
      </ErrorBoundary>
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
